import { useEffect, useMemo, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { HiOutlineArrowPath, HiOutlineDocumentArrowUp } from "react-icons/hi2";

import PageHeader from "../../../components/ui/pageHeader/PageHeader";
import PageLoader from "../../../components/ui/pageLoader/PageLoader";

import {
  getSchoolYears,
  setSelectedSchoolYear,
} from "../../../redux/features/schoolYears/schoolYearSlice";

import {
  analyzeExcel,
  importExcel,
  resetExcelImport,
} from "../redux/excelSlice";

import ExcelStepIndicator from "../components/ExcelStepIndicator";
import ExcelUploadCard from "../components/ExcelUploadCard";
import ExcelErrorsCard from "../components/ExcelErrorsCard";
import ExcelWarningsCard from "../components/ExcelWarningsCard";
import ExcelColumnsTable from "../components/ExcelColumnsTable";
import ExcelFieldSuggestions from "../components/ExcelFieldSuggestions";
import ExcelRowPreview from "../components/ExcelRowPreview";
import ExcelImportControls from "../components/ExcelImportControls";
import ExcelImportSummary from "../components/ExcelImportSummary";

import "../styles/ExcelImportPage.css";

export default function ExcelImportPage() {
  const dispatch = useDispatch();

  const [file, setFile] = useState(null);

  const [autoCreateFields, setAutoCreateFields] = useState(true);

  const [existingDossierMode, setExistingDossierMode] = useState("update");

  const { schoolYears, selectedSchoolYear } = useSelector(
    (state) => state.schoolYears,
  );

  const { analyzeResult, importResult, isAnalyzing, isImporting, error } =
    useSelector((state) => state.excelImport);

  useEffect(() => {
    if (!schoolYears?.length) {
      dispatch(getSchoolYears());
    }

    return () => {
      dispatch(resetExcelImport());
    };
  }, [dispatch, schoolYears]);

  useEffect(() => {
    if (selectedSchoolYear?._id || !schoolYears?.length) return;

    const activeSchoolYear = schoolYears.find(
      (schoolYear) => schoolYear.is_active,
    );

    if (activeSchoolYear) {
      dispatch(setSelectedSchoolYear(activeSchoolYear));
    }
  }, [dispatch, schoolYears, selectedSchoolYear]);

  const hasBlockingErrors = useMemo(() => {
    return (
      Array.isArray(analyzeResult?.errors) && analyzeResult.errors.length > 0
    );
  }, [analyzeResult]);

  const currentStep = useMemo(() => {
    if (importResult) return 4;
    if (analyzeResult) return 3;
    if (file && selectedSchoolYear?._id) return 2;
    return 1;
  }, [file, selectedSchoolYear, analyzeResult, importResult]);

  const handleAnalyze = async () => {
    if (!file || !selectedSchoolYear?._id) return;

    await dispatch(
      analyzeExcel({
        schoolYearId: selectedSchoolYear._id,
        file,
      }),
    ).unwrap();
  };

  const handleImport = async () => {
    if (
      !file ||
      !selectedSchoolYear?._id ||
      !analyzeResult ||
      hasBlockingErrors
    ) {
      return;
    }

    await dispatch(
      importExcel({
        schoolYearId: selectedSchoolYear._id,
        file,
        autoCreateFields,
        existingDossierMode,
      }),
    ).unwrap();
  };

  if (!schoolYears) {
    return <PageLoader title="Excel import laden..." />;
  }

  return (
    <div className="excel-import-page">
      <PageHeader
        eyebrow="POWER"
        title="Excel import"
        description="Analyseer Excel-bestanden, controleer veldmapping en importeer dossiers veilig."
      />

      <ExcelStepIndicator currentStep={currentStep} />

      {error && <div className="excel-import-page__error">{error}</div>}

      <ExcelUploadCard
        schoolYears={schoolYears}
        selectedSchoolYear={selectedSchoolYear}
        onSelectSchoolYear={(schoolYearId) => {
          const schoolYear = schoolYears.find(
            (item) => item._id === schoolYearId,
          );
          dispatch(setSelectedSchoolYear(schoolYear || null));
        }}
        file={file}
        onFileChange={setFile}
        isAnalyzing={isAnalyzing}
        onAnalyze={handleAnalyze}
      />

      {(isAnalyzing || isImporting) && (
        <div className="excel-import-page__loading">
          <HiOutlineArrowPath />
          <span>
            {isAnalyzing ? "Excel analyseren..." : "Excel importeren..."}
          </span>
        </div>
      )}

      {analyzeResult && (
        <div className="excel-import-page__review">
          <ExcelErrorsCard errors={analyzeResult.errors || []} />

          <ExcelWarningsCard warnings={analyzeResult.warnings || []} />

          <ExcelColumnsTable columns={analyzeResult.columns || []} />

          <ExcelFieldSuggestions
            suggestions={analyzeResult.new_field_suggestions || []}
          />

          <ExcelRowPreview rows={analyzeResult.row_preview || []} />

          <ExcelImportControls
            autoCreateFields={autoCreateFields}
            onAutoCreateFieldsChange={setAutoCreateFields}
            existingDossierMode={existingDossierMode}
            onExistingDossierModeChange={setExistingDossierMode}
            disabled={hasBlockingErrors || isImporting}
            onImport={handleImport}
          />
        </div>
      )}

      {importResult && <ExcelImportSummary result={importResult} />}

      {!analyzeResult && (
        <div className="excel-import-page__info">
          <HiOutlineDocumentArrowUp />
          <div>
            <strong>Import start altijd met analyse.</strong>
            <p>
              POWER leest het Excel-bestand eerst in, detecteert kolommen,
              controleert fouten en toont een preview. Er wordt nog niets
              geïmporteerd tijdens de analyse.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
